import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popconfirm, Select, message } from "antd";
import dayjs from "dayjs";
import {
	AlertTriangle,
	ArrowLeft,
	Ban,
	CheckCircle2,
	ChevronRight,
	FileText,
	Loader2,
	Sparkles,
	XCircle
} from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PlanReviewExtractModal from "components/plan-review-extract-modal/plan-review-extract-modal";

import { useGetPlanReviewExtractionsSubscription } from "graphql/subscriptions/plan-review-extractions.generated";
import { useGetPlanReviewSubmissionTypesQuery } from "graphql/queries/get-plan-review-submission-types.generated";
import { useCancelPlanReviewExtractionMutation } from "graphql/mutations/cancel-plan-review-extraction.generated";

import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import routeNames from "constants/route-names";
import { PlanReviewExtractionStatus } from "constants/field-values";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type Extraction = NonNullable<
	ReturnType<typeof useGetPlanReviewExtractionsSubscription>["data"]
>["plan_review_requirement_extractions"][number];

const STATUS_META: Record<string, { label: string; className: string; icon: typeof CheckCircle2 }> = {
	[PlanReviewExtractionStatus.EXTRACTING]: {
		label: "Reading",
		className: "prx-status-running",
		icon: Loader2
	},
	[PlanReviewExtractionStatus.READY]: {
		label: "Ready to review",
		className: "prx-status-ready",
		icon: CheckCircle2
	},
	[PlanReviewExtractionStatus.COMPLETED]: {
		label: "Completed",
		className: "prx-status-completed",
		icon: CheckCircle2
	},
	[PlanReviewExtractionStatus.CANCELLED]: {
		label: "Cancelled",
		className: "prx-status-cancelled",
		icon: Ban
	},
	[PlanReviewExtractionStatus.FAILED]: { label: "Failed", className: "prx-status-failed", icon: AlertTriangle }
};

const ALL_STATUSES = "all";

// Reading and failed runs have no filter of their own: they are transient, and the
// reviewer only looks for them in the full list.
const STATUS_FILTER_OPTIONS = [
	{ label: "All statuses", value: ALL_STATUSES },
	{ label: "Ready for review", value: PlanReviewExtractionStatus.READY },
	{ label: "Completed", value: PlanReviewExtractionStatus.COMPLETED },
	{ label: "Cancelled", value: PlanReviewExtractionStatus.CANCELLED }
];

const formatDate = (raw?: string | null): string => (raw ? dayjs(raw).format("MMM D, YYYY · HH:mm") : "—");

/**
 * Every run of the AI rule extraction for a jurisdiction.
 *
 * A run takes minutes and the reviewer is told they can leave the page, so this is the
 * place they come back to — and the record of what each set of rules was generated
 * from, which is the part that has to survive long after the run.
 */
const PlanReviewExtractions = () => {
	const navigate = useNavigate();
	const { jurisdictionId, picker, emptyReason } = usePlanReviewJurisdiction();
	// Tells Ollie what the reader is looking at, and for which authority.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The rule extractions screen: the list of runs that read an authority document to propose rules. The reader reviews what each one proposed before importing it.",
			jurisdictionId
		})
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	const [isExtractModalOpen, setIsExtractModalOpen] = useState(false);
	const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
	const [cancellingId, setCancellingId] = useState<string | null>(null);
	const [cancelExtraction] = useCancelPlanReviewExtractionMutation();

	const { data, loading } = useGetPlanReviewExtractionsSubscription({
		skip: !jurisdictionId,
		variables: { jurisdictionId }
	});

	const { data: typesData } = useGetPlanReviewSubmissionTypesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});
	const submissionTypes = useMemo(() => typesData?.plan_review_submission_types ?? [], [typesData]);

	const extractions = useMemo(() => data?.plan_review_requirement_extractions ?? [], [data]);
	const visibleExtractions = useMemo(
		() => (statusFilter === ALL_STATUSES ? extractions : extractions.filter(e => e.status === statusFilter)),
		[extractions, statusFilter]
	);

	const openExtraction = useCallback(
		(id: string) => navigate(routeNames.PLAN_REVIEW_RULES_EXTRACTION.replace(":extractionId", id)),
		[navigate]
	);

	// The list is a subscription, so the card updates on its own once the row changes.
	const handleCancel = useCallback(
		async (id: string) => {
			if (!canManage) return blockChange();
			setCancellingId(id);
			try {
				const result = await cancelExtraction({ variables: { id } });
				// No row back means it was no longer READY: someone imported it, or cancelled it first.
				if (!result.data?.update_plan_review_requirement_extractions_by_pk) {
					message.info("This extraction changed and can't be cancelled any more.");
					return;
				}
				message.success("Extraction cancelled.");
			} catch (error) {
				console.error("Extraction cancel failed:", error);
				message.error("We couldn't cancel the extraction. Please try again.");
			} finally {
				setCancellingId(null);
			}
		},
		[cancelExtraction, canManage, blockChange]
	);

	const openExtractModal = useCallback(() => {
		if (!canManage) return blockChange();
		setIsExtractModalOpen(true);
	}, [canManage, blockChange]);

	return (
		<>
			<OllieLayout>
				<div className="prx-page">
					<div className="prx-shell">
						<button className="prx-back" onClick={() => navigate(routeNames.PLAN_REVIEW_RULES)}>
							<ArrowLeft size={16} />
							<span>Back to rules</span>
						</button>

						<header className="prx-header">
							<div>
								<h1 className="prx-title">Rule Extractions</h1>
								<p className="prx-subtitle">
									Every run of the AI extraction for your jurisdiction, and what each one was generated from.
								</p>
							</div>
							<div className="prx-header-actions">
								{picker}
								<button
									className={`prx-btn prx-btn-primary${canManage ? "" : " prx-locked"}`}
									onClick={openExtractModal}
									aria-disabled={!canManage}
									disabled={!jurisdictionId}
								>
									<Sparkles size={16} />
									New extraction
								</button>
							</div>
						</header>

						{notice}

						{emptyReason ? (
							<div className="prx-empty">
								<p className="prx-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : loading && extractions.length === 0 ? (
							<div className="prx-empty">
								<Loader2 className="prx-spinner" />
								<p>Loading extractions…</p>
							</div>
						) : extractions.length === 0 ? (
							<div className="prx-empty">
								<p className="prx-empty-title">No extractions yet</p>
								<p>Upload your submittal guidelines and Ollie will propose the rules for you to review.</p>
							</div>
						) : (
							<>
								<div className="prx-filters">
									<div className="prx-filter">
										<label className="prx-label">Status</label>
										<Select
											className="prx-control"
											value={statusFilter}
											onChange={setStatusFilter}
											options={STATUS_FILTER_OPTIONS}
										/>
									</div>
								</div>

								{visibleExtractions.length === 0 ? (
									<div className="prx-empty">
										<p className="prx-empty-title">No extractions match this filter</p>
										<p>Pick another status to see the rest of your runs.</p>
									</div>
								) : (
									<div className="prx-list">
										{visibleExtractions.map(extraction => {
											const meta = STATUS_META[extraction.status] ?? STATUS_META[PlanReviewExtractionStatus.EXTRACTING];
											const StatusIcon = meta.icon;
											const documents = extraction.plan_review_extraction_documents;
											// Candidates neither imported nor skipped. Zero of these is what makes the
											// database call the run COMPLETED, so it only ever has a number to show while
											// the run is still waiting for its reviewer.
											const pending = extraction.pending_candidates.aggregate?.count ?? 0;
											const isReady = extraction.status === PlanReviewExtractionStatus.READY;
											const isCancelled = extraction.status === PlanReviewExtractionStatus.CANCELLED;

											return (
												<div key={extraction.id} className={`prx-card${isCancelled ? " prx-card-cancelled" : ""}`}>
													<button type="button" className="prx-card-open" onClick={() => openExtraction(extraction.id)}>
														<div className="prx-card-main">
															<div className="prx-card-head">
																<span className={`prx-status ${meta.className}`}>
																	<StatusIcon
																		size={14}
																		className={
																			extraction.status === PlanReviewExtractionStatus.EXTRACTING
																				? "prx-spin"
																				: undefined
																		}
																	/>
																	{meta.label}
																</span>
																<span className="prx-card-date">{formatDate(extraction.created_at)}</span>
																{extraction.user ? (
																	<span className="prx-card-author">
																		by {extraction.user.first_name} {extraction.user.last_name}
																	</span>
																) : null}
															</div>

															<p className="prx-card-sources">
																{documents.length > 0 ? (
																	<>
																		<FileText size={14} />
																		{documents.map(d => d.file_name).join(" · ")}
																	</>
																) : (
																	"From written instructions only"
																)}
															</p>

															{extraction.instructions ? (
																<p className="prx-card-instructions">{extraction.instructions}</p>
															) : null}

															{extraction.status === PlanReviewExtractionStatus.FAILED ? (
																<p className="prx-card-error">
																	{extraction.error_message ?? "The extraction couldn't be completed."}
																</p>
															) : null}
														</div>

														<div className="prx-card-metrics">
															<div className="prx-metric">
																<span className="prx-metric-label">Proposed</span>
																<span className="prx-metric-value">{extraction.candidate_count ?? 0}</span>
															</div>
															<div className="prx-metric">
																<span className="prx-metric-label">Imported</span>
																<span className="prx-metric-value">{extraction.imported_count ?? 0}</span>
															</div>
															<div className="prx-metric">
																<span className="prx-metric-label">Pending</span>
																<span className="prx-metric-value">{isReady && pending > 0 ? pending : "—"}</span>
															</div>
														</div>

														<ChevronRight className="prx-card-chevron" size={18} />
													</button>

													{/* Only a run still waiting for its reviewer can be discarded, and only by a manager */}
													{isReady && canManage ? (
														<Popconfirm
															title="Cancel this extraction?"
															description="Its candidates stay visible but can no longer be edited or imported. This can't be undone."
															okText="Cancel extraction"
															cancelText="Keep it"
															okButtonProps={{ danger: true }}
															onConfirm={() => handleCancel(extraction.id)}
														>
															<button
																type="button"
																className="prx-card-cancel"
																disabled={cancellingId === extraction.id}
																title="Cancel extraction"
																aria-label="Cancel extraction"
															>
																<XCircle size={18} />
															</button>
														</Popconfirm>
													) : null}
												</div>
											);
										})}
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</OllieLayout>

			<PlanReviewExtractModal
				open={isExtractModalOpen}
				jurisdictionId={jurisdictionId}
				submissionTypes={submissionTypes}
				onCancel={() => setIsExtractModalOpen(false)}
				onStarted={extractionId => {
					setIsExtractModalOpen(false);
					openExtraction(extractionId);
				}}
			/>

			<LoadingOverlay isLoading={false} />
		</>
	);
};

export default PlanReviewExtractions;
